import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { TaxDeclarationService } from '../../../api/services/tax-declaration.service';
import { TaxDeclarationResponse } from '../../../api/models/tax-declaration-response';
import { TaxDeclarationStatus } from '../../../api/models/tax-declaration-status';
import { StartTaxDeclarationCommand } from '../../../api/models/start-tax-declaration-command';
import {
  SteuernOverviewHighlightId,
  STEUERN_STEP_ORDER,
} from './steuern-storage.service';
import { HttpClient, HttpContext } from '@angular/common/http';
import { SKIP_ERROR_TOAST } from '../../../utils/http-context-tokens';
import { STEUERN_DOCUMENT_TYPE_IDS } from './steuern-document-schema.service';

export type SteuernLoadState = 'idle' | 'loading' | 'ready' | 'error';

@Injectable({ providedIn: 'root' })
export class SteuernApiService {
  private readonly declaration$ = new BehaviorSubject<TaxDeclarationResponse | null>(null);
  private readonly loadState$ = new BehaviorSubject<SteuernLoadState>('idle');

  readonly declaration = this.declaration$.asObservable();

  constructor(
    private readonly taxDeclaration: TaxDeclarationService,
    private readonly http: HttpClient
  ) {}

  get currentDeclaration(): TaxDeclarationResponse | null {
    return this.declaration$.value;
  }

  get taxDeclarationId(): string | null {
    return this.currentDeclaration?.id ?? null;
  }

  loadOrInitialize(voucherCode?: string, opportunityId?: string | null): Observable<TaxDeclarationResponse | null> {
    this.loadState$.next('loading');
    const year = new Date().getFullYear() - 1;
    const initBody = {
      year,
      voucherCode: voucherCode || null,
      opportunityId: opportunityId ?? null,
    };

    return this.taxDeclaration.apiTaxDeclarationLastGet$Json(
      {},
      new HttpContext().set(SKIP_ERROR_TOAST, true)
    ).pipe(
      switchMap((res) => {
        if (res?.id) {
          this.declaration$.next(res);
          this.loadState$.next('ready');
          return of(res);
        }
        return this.taxDeclaration.apiTaxDeclarationInitializePost$Json({
          body: initBody,
        }).pipe(
          map((id) => ({ id, year } as TaxDeclarationResponse)),
          tap((decl) => {
            this.declaration$.next(decl);
            this.loadState$.next('ready');
          })
        );
      }),
      catchError((err) => {
        const is404 = err?.status === 404 || err?.status === 204;
        if (is404 || !err?.status) {
          return this.taxDeclaration.apiTaxDeclarationInitializePost$Json({
            body: initBody,
          }).pipe(
            map((id) => ({ id, year } as TaxDeclarationResponse)),
            tap((decl) => {
              this.declaration$.next(decl);
              this.loadState$.next('ready');
            })
          );
        }
        this.loadState$.next('error');
        return of(null);
      })
    );
  }

  initialize(voucherCode?: string, opportunityId?: string | null): Observable<TaxDeclarationResponse | null> {
    this.loadState$.next('loading');
    const year = new Date().getFullYear() - 1;
    const initBody = {
      year,
      voucherCode: voucherCode || null,
      opportunityId: opportunityId ?? null,
    };
    return this.taxDeclaration.apiTaxDeclarationInitializePost$Json({ body: initBody }).pipe(
      map((id) => ({ id, year } as TaxDeclarationResponse)),
      tap((decl) => {
        this.declaration$.next(decl);
        this.loadState$.next('ready');
      }),
      catchError(() => {
        this.loadState$.next('error');
        return of(null);
      })
    );
  }

  initializeDocument(file: File): Observable<string> {
    const year = new Date().getFullYear() - 1;
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('year', String(year));
    // "Letztjährige Steuererklärung hochladen" always uploads last year's return.
    formData.append('documentTypeId', STEUERN_DOCUMENT_TYPE_IDS.SteuererklaerungVorjahr);

    return this.http.post<string>(
      `${this.taxDeclaration.rootUrl}${TaxDeclarationService.ApiTaxDeclarationInitializeDocumentPostPath}`,
      formData
    );
  }

  refreshLast(): Observable<TaxDeclarationResponse | null> {
    return this.taxDeclaration.apiTaxDeclarationLastGet$Json(
      {},
      new HttpContext().set(SKIP_ERROR_TOAST, true)
    ).pipe(
      tap((res) => this.declaration$.next(res ?? null)),
      catchError(() => {
        this.declaration$.next(null);
        return of(null);
      })
    );
  }

  getDocumentReviewSrc(declaration: TaxDeclarationResponse | null = this.currentDeclaration): string | null {
    if (!declaration) {
      return null;
    }

    const documentValue = declaration.document as unknown;
    if (typeof documentValue === 'string' && documentValue.trim()) {
      return documentValue.trim();
    }

    const documentLink =
      typeof documentValue === 'object' &&
      documentValue !== null &&
      'link' in documentValue &&
      typeof documentValue.link === 'string'
        ? documentValue.link.trim()
        : '';

    if (documentLink) {
      return documentLink;
    }

    const fallbackLink = declaration.documentToReview?.trim();
    return fallbackLink || null;
  }



  rejectCurrentDeclaration(): Observable<void> {
    const rejectById = (id: string): Observable<void> =>
      this.taxDeclaration.apiTaxDeclarationIdRejectPost({ id }).pipe(
        switchMap(() => this.refreshLast()),
        map(() => undefined)
      );

    const currentId = this.taxDeclarationId;
    if (currentId) {
      return rejectById(currentId);
    }

    return this.refreshLast().pipe(
      switchMap((declaration) => {
        const id = declaration?.id;
        if (!id) {
          return new Observable<void>((obs) => {
            obs.error(new Error('No tax declaration id'));
            obs.complete();
          });
        }

        return rejectById(id);
      })
    );
  }

  getDocumentToSignSrc(declaration: TaxDeclarationResponse | null = this.currentDeclaration): string | null {
    if (!declaration) {
      return null;
    }

    // `documentToSign` is not yet part of the generated OpenAPI types; read defensively.
    const raw = (declaration as unknown as Record<string, unknown>)['documentToSign'];

    if (typeof raw === 'string' && raw.trim()) {
      return raw.trim();
    }

    if (
      typeof raw === 'object' &&
      raw !== null &&
      'link' in raw &&
      typeof (raw as { link?: unknown }).link === 'string'
    ) {
      const link = (raw as { link: string }).link.trim();
      if (link) {
        return link;
      }
    }

    // Fall back to the original document link if no dedicated sign document is available yet.
    return this.getDocumentReviewSrc(declaration);
  }

  approveCurrentDeclaration(): Observable<void> {
    const approveById = (id: string): Observable<void> =>
      this.taxDeclaration.apiTaxDeclarationIdApprovePost({ id }).pipe(
        switchMap(() => this.refreshLast()),
        map(() => undefined)
      );

    const currentId = this.taxDeclarationId;
    if (currentId) {
      return approveById(currentId);
    }

    return this.refreshLast().pipe(
      switchMap((declaration) => {
        const id = declaration?.id;
        if (!id) {
          return new Observable<void>((obs) => {
            obs.error(new Error('No tax declaration id'));
            obs.complete();
          });
        }

        return approveById(id);
      })
    );
  }

  deleteCurrentDeclaration(): Observable<void> {
    const id = this.taxDeclarationId;
    if (!id) {
      return new Observable((obs) => {
        obs.error(new Error('No tax declaration id'));
        obs.complete();
      });
    }
    return this.taxDeclaration.apiTaxDeclarationIdDelete({ id }).pipe(
      tap(() => this.declaration$.next(null)),
      map(() => undefined)
    );
  }

  saveStart(command: {
    canton?: string | null;
    pid?: string | null;
    ahvNumber?: string | null;
    accessCode?: string | null;
    extensionDate?: { year?: number; month?: number; day?: number } | string | null;
    hasExtensionSubmitted?: boolean;
  }): Observable<void> {
    const id = this.taxDeclarationId;
    if (!id) {
      return new Observable((obs) => {
        obs.error(new Error('No tax declaration id'));
        obs.complete();
      });
    }
    // Always send extension date as ISO string (YYYY-MM-DD) for /start
    const extensionDatePayload = this.toExtensionDateIso(command.extensionDate);

    const body: StartTaxDeclarationCommand = {
      taxDeclarationId: id,
      canton: command.canton ?? '',
      pid: command.pid ?? null,
      ahvNumber: command.ahvNumber ?? null,
      accessCode: command.accessCode ?? null,
      extensionDate: extensionDatePayload ?? null,
      hasExtensionSubmitted: command.hasExtensionSubmitted ?? false,
      voucherCode: null,
    };

    return this.taxDeclaration.apiTaxDeclarationStartPatch({ body }).pipe(
      switchMap(() => this.refreshLast()),
      map(() => undefined)
    );
  }

  applyVoucherCode(voucherCode: string): Observable<void> {
    const decl = this.currentDeclaration;
    const id = this.taxDeclarationId;
    if (!id || !decl) {
      return new Observable((obs) => {
        obs.error(new Error('No tax declaration'));
        obs.complete();
      });
    }
    const extensionDatePayload = this.toExtensionDateIso(
      decl.extensionDate as { year?: number; month?: number; day?: number } | string | null | undefined
    );
    const body: StartTaxDeclarationCommand = {
      taxDeclarationId: id,
      canton: decl.canton ?? '',
      ahvNumber: decl.ahvNumber ?? null,
      pid: decl.pid ?? null,
      accessCode: decl.accessCode ?? null,
      hasExtensionSubmitted: decl.hasExtensionSubmitted ?? false,
      extensionDate: extensionDatePayload ?? null,
      voucherCode: voucherCode.trim() || null,
    };
    return this.taxDeclaration.apiTaxDeclarationStartPatch({ body }).pipe(
      switchMap(() => this.refreshLast()),
      map(() => undefined)
    );
  }

  isStartComplete(): boolean {
    const d = this.currentDeclaration;
    if (!d) return false;
    const hasCanton = (d.canton ?? '').toString().trim() !== '';
    return hasCanton;
  }

  stepStatus(stepId: SteuernOverviewHighlightId): TaxDeclarationStatus | undefined {
    const d = this.currentDeclaration;
    if (!d) return undefined;
    switch (stepId) {
      case 'personal-information':
        return d.personalInformationStatus;
      case 'income':
        return d.incomeStatus;
      case 'assets':
        return d.assetsStatus;
      case 'health':
        return d.healthStatus;
      case 'other':
        return d.otherStatus;
      default:
        return undefined;
    }
  }

  isStepComplete(stepId: SteuernOverviewHighlightId): boolean {
    return this.stepStatus(stepId) === TaxDeclarationStatus.Complete;
  }

  getCompletedStepIds(): SteuernOverviewHighlightId[] {
    const ids: SteuernOverviewHighlightId[] = [];
    for (const id of STEUERN_STEP_ORDER) {
      if (this.isStepComplete(id)) ids.push(id);
    }
    return ids;
  }

  isOrderSubmitted(): boolean {
    return this.currentDeclaration?.status === TaxDeclarationStatus.Complete ||
      this.currentDeclaration?.status === TaxDeclarationStatus.SmzhInReview ||
      this.currentDeclaration?.status === TaxDeclarationStatus.MissingData ||
      this.currentDeclaration?.status === TaxDeclarationStatus.ClientSubmitted ||
      this.currentDeclaration?.status === TaxDeclarationStatus.ClientInReview ||
      this.currentDeclaration?.status === TaxDeclarationStatus.SentToClient ||
      this.currentDeclaration?.status === TaxDeclarationStatus.ClientAccepted;
  }

  /** True when declaration status is Complete (finished); false when InReview (submitted, in review). */
  isDeclarationComplete(): boolean {
    return this.currentDeclaration?.status === TaxDeclarationStatus.Complete;
  }

  isDeclarationReadOnly(): boolean {
    return this.currentDeclaration?.status === TaxDeclarationStatus.SmzhInReview ||
      this.currentDeclaration?.status === TaxDeclarationStatus.MissingData ||
      this.currentDeclaration?.status === TaxDeclarationStatus.ClientSubmitted ||
      this.currentDeclaration?.status === TaxDeclarationStatus.SentToClient ||
      this.currentDeclaration?.status === TaxDeclarationStatus.Complete;
  }

  getVoucherCode(): string {
    return (this.currentDeclaration?.voucherCode ?? '').toString();
  }

  isStepDataComplete(stepId: SteuernOverviewHighlightId): boolean {
    return this.isStepComplete(stepId);
  }

  isStepUnlocked(stepId: SteuernOverviewHighlightId): boolean {
    if (stepId === 'personal-information') return true;
    return this.isStepComplete('personal-information');
  }

  getFirstIncompleteStepPath(): string {
    for (const id of STEUERN_STEP_ORDER) {
      if (!this.isStepComplete(id)) return `/steuern/${id}`;
    }
    return '/steuern/overview';
  }

  isViewOnlyStepAccessAllowed(): boolean {
    return this.isOrderSubmitted();
  }

  /**
   * Calls PATCH /api/TaxDeclaration/finalize with the current taxDeclarationId.
   * After success, refreshes the declaration so /last returns status SmzhInReview.
   */
  finalizeTaxDeclaration(): Observable<void> {
    const id = this.taxDeclarationId;
    if (!id) {
      return new Observable((obs) => {
        obs.error(new Error('No tax declaration id'));
        obs.complete();
      });
    }
    return this.taxDeclaration.apiTaxDeclarationFinalizePatch({
      body: { taxDeclarationId: id },
    }).pipe(
      switchMap(() => this.refreshLast()),
      map(() => undefined)
    );
  }

  /** Normalize extension date to ISO string YYYY-MM-DD for /start payload. */
  private toExtensionDateIso(
    value: { year?: number; month?: number; day?: number } | string | null | undefined
  ): string | undefined {
    if (value == null) return undefined;
    if (typeof value === 'string') {
      const s = value.trim().slice(0, 10);
      return s || undefined;
    }
    const y = value.year;
    const m = value.month;
    const d = value.day;
    if (y == null || m == null || d == null) return undefined;
    const yy = String(y);
    const mm = String(m).padStart(2, '0');
    const dd = String(d).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  }
}
